import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Star,
  PhoneInTalk,
  SmartToy,
  ChatBubbleOutline,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import Logo from '../components/Logo';

// ============================================================================
// HARDCODED PRICING DATA
// ============================================================================

const PRICING_DATA = {
  ua: {
    currency: 'USD',
    symbol: '$',
    warDiscount: true,
    tiers: {
      basic: { monthly: 11, annual: 9, originalMonthly: 15, originalAnnual: 12, minUsers: 3 },
      pro: { monthly: 22, annual: 18, minUsers: 3 },
      corporate: { monthly: 34, annual: 28, minUsers: 10 },
    },
    addons: {
      aiConversation: { price: 11, perUser: true },
      omnichannelChat: { price: 7, perUser: true },
      callback: { price: 10, perUser: false },
    },
  },
  eu: {
    currency: 'EUR',
    symbol: '€',
    tiers: {
      basic: { monthly: 20, annual: 17, minUsers: 3 },
      pro: { monthly: 34, annual: 28, minUsers: 3 },
      corporate: { monthly: 49, annual: 41, minUsers: 10 },
    },
    addons: {
      aiConversation: { price: 11, perUser: true },
      omnichannelChat: { price: 10, perUser: true },
      callback: { price: 20, perUser: false, prefix: 'від ' },
    },
  },
};

const FEATURES = [
  // Core - included or priced
  { key: 'aiConversation', name: 'AI Conversation Intelligence', basic: 'price:aiConversation', pro: true, corporate: true },
  { key: 'omnichannelChat', name: 'Omnichannel Chat', basic: 'price:omnichannelChat', pro: true, corporate: true },
  // Pro features
  { key: 'realTimeReports', name: 'Real-time Reports', basic: false, pro: true, corporate: true },
  { key: 'agentStatuses', name: 'Agent Statuses', basic: false, pro: true, corporate: true },
  { key: 'callWhisper', name: 'Call Whisper', basic: false, pro: true, corporate: true },
  { key: 'csat', name: 'CSAT', basic: false, pro: true, corporate: true },
  { key: 'customerSuccessTeam', name: 'Customer Success Team', basic: false, pro: true, corporate: true },
  { key: 'aiAnalyst', name: 'AI Analyst', basic: false, pro: true, corporate: true, comingSoon: true },
  { key: 'callback', name: 'Callback', basic: 'price:callback', pro: true, corporate: true },
  // Corporate only
  { key: 'powerDialer', name: 'Power Dialer', basic: false, pro: false, corporate: true },
  { key: 'autoMissedProcessing', name: 'Automated Missed Calls Processing', basic: false, pro: false, corporate: true, comingSoon: true },
  { key: 'prioritySupport', name: 'Priority Support', basic: false, pro: false, corporate: true },
  { key: 'dedicatedVipCs', name: 'Dedicated Customer Success (VIP)', basic: false, pro: false, corporate: true },
  { key: 'customLookerReports', name: 'Custom Looker Studio Reports', basic: false, pro: false, corporate: true },
  { key: 'customCrmIntegration', name: 'Custom CRM Integration', basic: false, pro: false, corporate: true },
  { key: 'robocall', name: 'Robocall', basic: 'addon', pro: 'addon', corporate: true },
  { key: 'advancedAiConversation', name: 'Advanced 360 AI Conversation Intelligence', subtitle: 'replaces AI Conversation Intelligence', basic: 'addon', pro: 'addon', corporate: true },
];

// Standalone Add-ons (separate section)
const ADDONS = {
  ua: [
    { key: 'callTracking', name: 'Call Tracking', icon: 'phone', price: 'від $18/міс', description: 'Відстежуйте джерела дзвінків та оптимізуйте маркетинг' },
    { key: 'aiVoicebot', name: 'AI Voicebot', icon: 'voicebot', price: 'від $0.25/хв', description: 'Автоматизуйте вхідні та вихідні дзвінки з ШІ' },
    { key: 'aiChatbot', name: 'AI Chatbot', icon: 'chat', price: 'від $300/міс', description: 'Розумний чат-бот для підтримки клієнтів 24/7' },
  ],
  eu: [
    { key: 'callTracking', name: 'Call Tracking', icon: 'phone', price: 'від €25/міс', description: 'Відстежуйте джерела дзвінків та оптимізуйте маркетинг' },
    { key: 'aiVoicebot', name: 'AI Voicebot', icon: 'voicebot', price: 'від €0.25/хв', description: 'Автоматизуйте вхідні та вихідні дзвінки з ШІ' },
    { key: 'aiChatbot', name: 'AI Chatbot', icon: 'chat', price: 'від €300/міс', description: 'Розумний чат-бот для підтримки клієнтів 24/7' },
  ],
};

const PLAN_INFO = {
  basic: {
    name: 'Basic',
    description: 'Для малих команд',
  },
  pro: {
    name: 'Pro',
    description: 'Для зростаючого бізнесу',
    popular: true,
  },
  corporate: {
    name: 'Corporate',
    description: 'Для великих організацій',
  },
};

// ============================================================================
// STYLES
// ============================================================================

const cardStyles = {
  base: {
    p: 4,
    borderRadius: 4,
    bgcolor: '#fff',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    transition: 'all 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
    },
  },
  pro: {
    p: 4,
    borderRadius: 4,
    bgcolor: 'rgba(0, 200, 83, 0.04)',
    border: '2px solid #00C853',
    boxShadow: '0 0 30px rgba(0, 200, 83, 0.15)',
    transition: 'all 0.3s ease',
    transform: 'scale(1.02)',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    position: 'relative',
    '&:hover': {
      transform: 'scale(1.02) translateY(-4px)',
      boxShadow: '0 0 40px rgba(0, 200, 83, 0.25)',
    },
  },
};

// ============================================================================
// COMPONENTS
// ============================================================================

const PricingCard = ({ plan, pricing, isAnnual, symbol }) => {
  const info = PLAN_INFO[plan];
  const tierPricing = pricing.tiers[plan];
  const price = isAnnual ? tierPricing.annual : tierPricing.monthly;
  const originalPrice = isAnnual ? tierPricing.originalAnnual : tierPricing.originalMonthly;
  const hasDiscount = originalPrice && pricing.warDiscount;
  const isPro = plan === 'pro';
  const isCorporate = plan === 'corporate';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: plan === 'basic' ? 0 : plan === 'pro' ? 0.1 : 0.2 }}
      style={{ height: '100%' }}
    >
      <Paper elevation={0} sx={isPro ? cardStyles.pro : cardStyles.base}>
        {/* Popular Badge */}
        {isPro && (
          <Box
            sx={{
              position: 'absolute',
              top: -12,
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'linear-gradient(135deg, #00C853 0%, #29B6F6 100%)',
              color: '#fff',
              px: 2,
              py: 0.5,
              borderRadius: 2,
              fontSize: '0.75rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              boxShadow: '0 4px 12px rgba(0, 200, 83, 0.3)',
            }}
          >
            <Star sx={{ fontSize: 14 }} />
            Найпопулярніший
          </Box>
        )}

        {/* Plan Name */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            mb: 1,
            color: isPro ? '#00C853' : '#222',
            mt: isPro ? 2 : 0,
          }}
        >
          {info.name}
        </Typography>

        {/* Description */}
        <Typography
          variant="body2"
          sx={{ color: '#666', mb: 3, fontStyle: 'italic' }}
        >
          {info.description}
        </Typography>

        {/* Price */}
        <Box sx={{ mb: 3 }}>
          {/* Strikethrough original price for war discount */}
          {hasDiscount && (
            <Typography
              variant="h5"
              sx={{
                color: '#999',
                textDecoration: 'line-through',
                fontWeight: 500,
                mb: 0.5,
              }}
            >
              {symbol}{originalPrice}
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: '#00C853',
                lineHeight: 1,
              }}
            >
              {symbol}{price}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: '#666' }}
            >
              /user
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{ color: '#999', display: 'block', mt: 0.5 }}
          >
            {isAnnual ? 'при річній оплаті' : 'щомісячна оплата'}
          </Typography>
          {/* War discount badge */}
          {hasDiscount && (
            <Chip
              icon={<span style={{ marginLeft: 8 }}>🇺🇦</span>}
              label="Підтримуємо український бізнес"
              size="small"
              sx={{
                mt: 1.5,
                bgcolor: 'rgba(0, 87, 183, 0.08)',
                color: '#0057B7',
                fontWeight: 500,
                fontSize: '0.7rem',
                '& .MuiChip-icon': {
                  fontSize: '1rem',
                },
              }}
            />
          )}
        </Box>

        {/* Min Users */}
        <Typography
          variant="body2"
          sx={{ color: '#666', mb: 3 }}
        >
          Мінімум {tierPricing.minUsers} користувачів
        </Typography>

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* CTA Button */}
        <Button
          variant={isCorporate ? 'outlined' : 'contained'}
          fullWidth
          sx={{
            py: 1.5,
            borderRadius: 2,
            fontWeight: 600,
            fontSize: '1rem',
            textTransform: 'none',
            ...(isCorporate
              ? {
                  borderColor: '#00C853',
                  color: '#00C853',
                  '&:hover': {
                    borderColor: '#00A040',
                    bgcolor: 'rgba(0, 200, 83, 0.04)',
                  },
                }
              : {
                  bgcolor: '#00C853',
                  '&:hover': {
                    bgcolor: '#00A040',
                    transform: 'scale(1.02)',
                  },
                }),
            transition: 'all 0.2s ease',
          }}
        >
          {isCorporate ? "Зв'язатися" : 'Почати'}
        </Button>
      </Paper>
    </motion.div>
  );
};

const FeatureCell = ({ value, symbol, addons }) => {
  // Check if it's a price reference
  if (typeof value === 'string' && value.startsWith('price:')) {
    const addonKey = value.replace('price:', '');
    const addonData = addons[addonKey];
    const price = addonData?.price || addonData;
    const perUser = addonData?.perUser;
    const prefix = addonData?.prefix || '';

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
        <Chip
          label={`${prefix}+${symbol}${price}`}
          size="small"
          sx={{
            bgcolor: 'rgba(0, 200, 83, 0.1)',
            color: '#00C853',
            fontWeight: 600,
            fontSize: '0.8rem',
          }}
        />
        {perUser && (
          <Typography variant="caption" sx={{ color: '#999', fontSize: '0.65rem' }}>
            /user/міс
          </Typography>
        )}
        {!perUser && addonData?.price && (
          <Typography variant="caption" sx={{ color: '#999', fontSize: '0.65rem' }}>
            /міс
          </Typography>
        )}
      </Box>
    );
  }

  // Addon text
  if (value === 'addon') {
    return (
      <Chip
        label="Add-on"
        size="small"
        variant="outlined"
        sx={{
          borderColor: '#ccc',
          color: '#666',
          fontSize: '0.75rem',
        }}
      />
    );
  }

  // Included (true)
  if (value === true) {
    return (
      <CheckCircle
        sx={{
          color: '#00C853',
          fontSize: 24,
          transition: 'transform 0.2s ease',
          '&:hover': {
            transform: 'scale(1.2)',
          },
        }}
      />
    );
  }

  // Not available (false)
  return (
    <Cancel
      sx={{
        color: '#ddd',
        fontSize: 24,
      }}
    />
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const PricingPage = () => {
  const [region, setRegion] = useState('ua');
  const [isAnnual, setIsAnnual] = useState(true);

  const pricing = PRICING_DATA[region];
  const { symbol, addons } = pricing;

  // Calculate savings percentage
  const savings = Math.round(
    ((pricing.tiers.pro.monthly - pricing.tiers.pro.annual) / pricing.tiers.pro.monthly) * 100
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      {/* Header */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: '#fff',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Logo height={36} />
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: '#222', ml: 1 }}
            >
              Ringostat
            </Typography>
          </Box>

          {/* Region Toggle */}
          <ToggleButtonGroup
            value={region}
            exclusive
            onChange={(e, val) => val && setRegion(val)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                px: 2,
                py: 0.5,
                borderRadius: '8px !important',
                border: 'none',
                bgcolor: '#f5f7fa',
                color: '#666',
                fontWeight: 500,
                '&.Mui-selected': {
                  bgcolor: '#00C853',
                  color: '#fff',
                  '&:hover': {
                    bgcolor: '#00A040',
                  },
                },
              },
            }}
          >
            <ToggleButton value="ua">UA</ToggleButton>
            <ToggleButton value="eu">EU</ToggleButton>
          </ToggleButtonGroup>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: '#222',
                mb: 2,
              }}
            >
              Прозоре та зрозуміле ціноутворення
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#666',
                fontWeight: 400,
                mb: 4,
              }}
            >
              Оберіть план, який підходить вашому бізнесу
            </Typography>
          </motion.div>

          {/* Billing Toggle - Subtle */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              bgcolor: '#fff',
              px: 2,
              py: 1,
              borderRadius: 3,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: !isAnnual ? '#222' : '#999',
                fontWeight: !isAnnual ? 600 : 400,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onClick={() => setIsAnnual(false)}
            >
              Щомісячна
            </Typography>
            <Box
              onClick={() => setIsAnnual(!isAnnual)}
              sx={{
                width: 44,
                height: 24,
                borderRadius: 12,
                bgcolor: isAnnual ? '#00C853' : '#ccc',
                position: 'relative',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  bgcolor: '#fff',
                  position: 'absolute',
                  top: 2,
                  left: isAnnual ? 22 : 2,
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: isAnnual ? '#222' : '#999',
                  fontWeight: isAnnual ? 600 : 400,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onClick={() => setIsAnnual(true)}
              >
                Річна
              </Typography>
              {isAnnual && (
                <Chip
                  label={`-${savings}%`}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(0, 200, 83, 0.1)',
                    color: '#00C853',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    height: 20,
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>

        {/* Pricing Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 3,
            mb: 8,
            alignItems: 'stretch',
          }}
        >
          <PricingCard plan="basic" pricing={pricing} isAnnual={isAnnual} symbol={symbol} />
          <PricingCard plan="pro" pricing={pricing} isAnnual={isAnnual} symbol={symbol} />
          <PricingCard plan="corporate" pricing={pricing} isAnnual={isAnnual} symbol={symbol} />
        </Box>

        {/* Feature Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: '1px solid rgba(0, 0, 0, 0.08)',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                p: 3,
                borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                bgcolor: '#fff',
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#222' }}>
                Порівняння функцій
              </Typography>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: '#fafbfc',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600, color: '#222', width: '40%' }}>
                      Функція
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: '#222' }}>
                      Basic
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 600,
                        color: '#00C853',
                        bgcolor: 'rgba(0, 200, 83, 0.04)',
                      }}
                    >
                      Pro
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: '#222' }}>
                      Corporate
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {FEATURES.map((feature, index) => (
                    <TableRow
                      key={feature.key}
                      sx={{
                        bgcolor: feature.comingSoon
                          ? 'rgba(244, 67, 54, 0.06)'
                          : index % 2 === 0 ? '#fff' : '#fafbfc',
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                          bgcolor: feature.comingSoon
                            ? 'rgba(244, 67, 54, 0.1)'
                            : 'rgba(0, 200, 83, 0.04)',
                        },
                      }}
                    >
                      <TableCell sx={{ color: '#222' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: feature.comingSoon ? 500 : 400 }}>
                              {feature.name}
                            </Typography>
                            {feature.subtitle && (
                              <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic', display: 'block' }}>
                                {feature.subtitle}
                              </Typography>
                            )}
                          </Box>
                          {feature.comingSoon && (
                            <Chip
                              label="Coming Soon"
                              size="small"
                              sx={{
                                bgcolor: 'rgba(244, 67, 54, 0.1)',
                                color: '#f44336',
                                fontWeight: 600,
                                fontSize: '0.65rem',
                                height: 20,
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <FeatureCell value={feature.basic} symbol={symbol} addons={addons} />
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ bgcolor: feature.comingSoon ? 'transparent' : 'rgba(0, 200, 83, 0.02)' }}
                      >
                        <FeatureCell value={feature.pro} symbol={symbol} addons={addons} />
                      </TableCell>
                      <TableCell align="center">
                        <FeatureCell value={feature.corporate} symbol={symbol} addons={addons} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </motion.div>

        {/* Add-ons Section */}
        <Box sx={{ mt: 8 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#222', mb: 1, textAlign: 'center' }}>
            Додаткові модулі
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 4, textAlign: 'center' }}>
            Розширте можливості вашого бізнесу
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 3,
            }}
          >
            {ADDONS[region].map((addon) => {
              const IconComponent = addon.icon === 'phone' ? PhoneInTalk :
                                   addon.icon === 'voicebot' ? SmartToy :
                                   ChatBubbleOutline;
              return (
                <Paper
                  key={addon.key}
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    bgcolor: '#fff',
                    transition: 'all 0.3s ease',
                    textAlign: 'center',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
                      borderColor: '#00C853',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      bgcolor: 'rgba(0, 200, 83, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <IconComponent sx={{ color: '#00C853', fontSize: 32 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#222', mb: 1 }}>
                    {addon.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                    {addon.description}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#00C853' }}>
                    {addon.price}
                  </Typography>
                </Paper>
              );
            })}
          </Box>
          <Typography variant="body2" sx={{ textAlign: 'center', color: '#666', mt: 3 }}>
            * Доступні для всіх тарифів
          </Typography>
        </Box>

        {/* Footer CTA */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography variant="h6" sx={{ color: '#222', mb: 1 }}>
            Потрібне індивідуальне ціноутворення?
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
            Зв'яжіться з нашою командою для обговорення спеціальних умов
          </Typography>
          <Button
            variant="outlined"
            sx={{
              borderColor: '#00C853',
              color: '#00C853',
              px: 4,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              '&:hover': {
                borderColor: '#00A040',
                bgcolor: 'rgba(0, 200, 83, 0.04)',
              },
            }}
          >
            Зв'язатися з нами
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default PricingPage;
